/*
 * Paperbloom AI studio — native storefront port of the Lovable PatternGenerator.
 *
 * Talks to the same backend the iframe used (Supabase lhkttcdbbvammypbgkus):
 *   sign-in   generate-multipass-token -> shopify-multipass -> /auth/v1/verify (magiclink token_hash)
 *   credits   sync-user-credits, then user_credits via REST
 *   pieces    generate-pattern (10 transparent PNGs, 10 credits; redraw one = 1 credit)
 *             finalize-asset-storage per asset, fire-and-forget (keeps the user's library intact)
 *   print     composed here with the exact PatternGenerator math (1425px canvas, 120px base piece,
 *             template placements + toroidal wrap), exported at 2x = 2850px JPEG q0.9 and a 0.5x
 *             712px JPEG q0.7 thumb, both uploaded to the public `designs` bucket.
 *   cart      /cart/add.js with the same line properties current AI orders carry.
 * Unlike the app, the production JPG is uploaded BEFORE the line is added, so an order can never
 * point at a file that failed to upload.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-pb-ai]');
  if (!root) return;
  const CFG = JSON.parse(root.querySelector('[data-pb-ai-config]').textContent);
  const SB = String(CFG.supabaseUrl || '').replace(/\/+$/, '');
  const KEY = CFG.anonKey || '';
  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));

  // ---------------------------------------------------------------- constants
  const CANVAS = 1425;          // PatternGenerator fabric canvas size
  const BASE = 120;             // baseSize constant in applyTemplateToCanvas
  const PROD_MULT = 2;          // production export multiplier
  const THUMB_MULT = 0.5;       // cart thumbnail multiplier
  // The print is the design tile repeated 2 across and 2 down in one 6000 px square (300 dpi, 20 in), like the
  // Product Builder's sheets. The browser draws one 3000 px tile (a phone can't hold a 6000 px canvas) and the relay
  // lays it out: pb-studio-relay /api/print-file -> lib/print-jpeg.js makeRepeatJpeg.
  const TILE_PX = 3000;
  const PRINT_ACROSS = 2; // tiles across (and down) one 20 in print sheet: the mockups show the tile at 10 in
  const PRINT_RELAY = 'https://pb-studio-relay.vercel.app/api/print-file';
  const LAYOUTS = [
    { id: 'Grid', label: 'Grid', dots: [[6, 6, 0], [28, 6, 0], [6, 28, 0], [28, 28, 0]] },
    { id: 'Horizontal Brick', label: 'Brick', dots: [[2, 6, 0], [22, 6, 0], [12, 20, 0], [32, 20, 0], [2, 34, 0], [22, 34, 0]] },
    { id: 'Diagonal Brick', label: 'Diagonal', dots: [[5, 5, 45], [17, 17, 45], [29, 29, 45], [29, 5, 45], [5, 29, 45]] },
    { id: 'Scattered', label: 'Scattered', dots: [[4, 8, 20], [26, 3, -15], [15, 20, 10], [30, 28, -25], [6, 32, 35]] },
  ];
  const FALLBACK_STYLES = [
    { slug: 'retro-comic', label: 'Retro comic' },
    { slug: 'pixar', label: '3D cartoon' },
    { slug: 'vintage-christmas-illustration', label: 'Vintage Christmas' },
    { slug: 'holiday-nostalgia', label: 'Holiday nostalgia' },
  ];
  const FONTS = [
    { id: 'Montserrat', label: 'Bold', css: '800 150px Montserrat', q: 'Montserrat:wght@800' },
    { id: 'PT Serif', label: 'Classic', css: '700 150px "PT Serif"', q: 'PT+Serif:wght@700' },
    { id: 'Dancing Script', label: 'Script', css: '700 170px "Dancing Script"', q: 'Dancing+Script:wght@700' },
    { id: 'Pacifico', label: 'Retro', css: '400 150px Pacifico', q: 'Pacifico' },
    { id: 'Fredoka', label: 'Bubbly', css: '600 150px Fredoka', q: 'Fredoka:wght@600' },
  ];
  const INKS = ['#6B2E2E', '#E4553C', '#F78BA1', '#2F6F8F', '#2E6B45', '#E0A526', '#1E1C1D', '#FFFFFF'];
  const TIPS = [
    'Dreaming up ideas for them',
    'Sketching their name in big letters',
    'Drawing characters and little details',
    'Adding color',
    'Cleaning up the edges so they tile nicely',
    'Almost there, the last pieces are the slowest',
  ];
  const DRAFT_KEY = 'pbai.draft.v1' + (CFG.template ? ':' + CFG.template.productId : '');
  const DESIGNS_KEY = 'pbai.designs.v1';
  const STYLES_SHOWN = 8;
  const LIVE_PX = 1024;          // live-mockup source tile size
  const SESSION_KEY = 'pbai.session.v1';

  // --------------------------------------------------------------------- state
  const S = {
    step: 1, maxStep: 1,
    form: { occasion: '', name: '', theme: '', message: '', artStyle: 'retro-comic', mode: 'mixed' },
    pieces: [],           // {uid, kind:'ai'|'photo'|'text', src, prompt, isText, assetId, slot, picked}
    batchId: null,
    bg: '#ffffff',
    myBgs: [],            // colours the shopper picked with "Add a color"; they join the swatch row
    layout: 'Horizontal Brick',
    sizeV: 5, spaceV: 7, rot: 0,
    roll: null, qty: 1,
    credits: null,
    busy: false, busyPieces: new Set(),
    zoom: false,
    designId: null,
    stylesOpen: false,
    view: 'print',        // stage shows the print, or a live mockup scene id (s1..s5)
    added: false,
  };
  let session = null;
  let styles = FALLBACK_STYLES.slice();
  let pendingStyle = null; // a prefilled style slug that arrives before the live style list
  let raw = null;                 // template placements before wrapping (random, so cached)
  let placements = [];
  const imgCache = new Map();     // src -> HTMLImageElement
  let palette = [];

  // ------------------------------------------------------------------ helpers
  const money = (cents) => {
    const v = (cents / 100).toFixed(2);
    const fmt = CFG.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, v).replace(/<[^>]+>/g, '');
  };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const uid = () => Math.random().toString(36).slice(2, 10);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  // Size slider 1..10 -> PatternGenerator scale (0.5..6, default 2.5)
  const scaleOf = (v) => [1.2, 1.5, 1.8, 2.1, 2.5, 2.9, 3.3, 3.8, 4.4, 5][v - 1] || 2.5;
  // Spacing slider 1..10 (tight..roomy) -> inverted density (3.3 - density; default 0.8)
  const densOf = (v) => [2.1, 1.8, 1.55, 1.35, 1.15, 0.95, 0.8, 0.65, 0.5, 0.4][v - 1] || 0.8;
  const ssGet = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } };
  const ssSet = (k, v) => { try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } };

  function alertMsg(html, tone) {
    const el = $('[data-pb-ai-alert]');
    if (!html) { el.hidden = true; el.innerHTML = ''; return; }
    el.innerHTML = html;
    el.classList.toggle('is-ok', tone === 'ok');
    el.hidden = false;
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // ------------------------------------------------------------- tiny IDB kv
  // Pieces are ~1-2 MB base64 each, too big for localStorage, so drafts live in IndexedDB.
  const idb = (() => {
    let dbp = null;
    const open = () => dbp || (dbp = new Promise((res, rej) => {
      const r = indexedDB.open('pbai', 1);
      r.onupgradeneeded = () => r.result.createObjectStore('kv');
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    }));
    const tx = async (mode, fn) => {
      const db = await open();
      return new Promise((res, rej) => {
        const t = db.transaction('kv', mode);
        const req = fn(t.objectStore('kv'));
        t.oncomplete = () => res(req && req.result);
        t.onerror = () => rej(t.error);
      });
    };
    return {
      get: (k) => tx('readonly', (s) => s.get(k)).catch(() => null),
      set: (k, v) => tx('readwrite', (s) => s.put(v, k)).catch(() => null),
      del: (k) => tx('readwrite', (s) => s.delete(k)).catch(() => null),
    };
  })();

  const newId = () => 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  function snapshot() {
    return {
      id: S.designId, t: Date.now(), step: S.step, maxStep: S.maxStep, form: S.form, batchId: S.batchId, bg: S.bg, myBgs: S.myBgs,
      layout: S.layout, sizeV: S.sizeV, spaceV: S.spaceV, rot: S.rot, roll: S.roll, qty: S.qty, tpl: S.tpl || null,
      pieces: S.pieces.map(({ uid: u, kind, src, prompt, isText, assetId, origAssetId, slot, picked, nameTwin }) => ({ uid: u, kind, src, prompt, isText, assetId, origAssetId, slot, picked, nameTwin })),
    };
  }
  function applyDraft(d) {
    Object.assign(S, { designId: d.id || newId(), step: d.step || 1, maxStep: d.maxStep || 1,
      form: Object.assign({ occasion: '', name: '', theme: '', message: '', artStyle: 'retro-comic' }, d.form || {}, { mode: 'mixed' }),
      batchId: d.batchId || null, bg: d.bg || '#ffffff', myBgs: Array.isArray(d.myBgs) ? d.myBgs : [], layout: d.layout || 'Horizontal Brick', sizeV: d.sizeV || 5,
      spaceV: d.spaceV || 7, rot: d.rot || 0, roll: d.roll || null, qty: d.qty || 1, pieces: d.pieces || [], added: false, view: 'print', tpl: d.tpl || null });
    if (!S.pieces.length) { S.step = 1; S.maxStep = 1; }
    markNameTwins();
  }

  let saveTimer = null;
  function saveDraft() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => { idb.set(DRAFT_KEY, snapshot()); }, 400);
  }

  // "Your designs": every creation is kept in IndexedDB so starting over never loses work.
  let designs = [];
  async function loadDesigns() {
    const list = await idb.get(DESIGNS_KEY);
    designs = Array.isArray(list) ? list : [];
    renderDesignsCount();
  }
  async function thumbOf() {
    if (!placements.length) {
      const first = S.pieces.find((p) => p.picked) || S.pieces[0];
      return first ? first.src : null;
    }
    const c = document.createElement('canvas');
    c.width = c.height = 240;
    await paint(c.getContext('2d'), 240 / CANVAS);
    return c.toDataURL('image/jpeg', 0.8);
  }
  async function saveToDesigns() {
    if (!S.pieces.length) return false;
    if (!S.designId) S.designId = newId();
    const entry = Object.assign(snapshot(), { thumb: await thumbOf() });
    designs = [entry].concat(designs.filter((d) => d.id !== entry.id)).slice(0, 30);
    await idb.set(DESIGNS_KEY, designs);
    renderDesignsCount();
    return true;
  }
  function renderDesignsCount() {
    const b = $('[data-pb-ai-designs]');
    const n = designs.length;
    b.hidden = !n;
    $('[data-pb-ai-designs-n]').textContent = n;
  }
  function renderDesignsList() {
    const fmt = (t) => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    $('[data-pb-ai-designs-list]').innerHTML = designs.length ? designs.map((d) => {
      const f = d.form || {};
      const title = [f.occasion, f.name && `for ${f.name}`].filter(Boolean).join(' ') || 'Untitled design';
      const current = d.id === S.designId;
      return `<li class="pb-ai__design${current ? ' is-current' : ''}">
        <span class="pb-ai__design-img">${d.thumb ? `<img src="${esc(d.thumb)}" alt="">` : ''}</span>
        <span class="pb-ai__design-txt"><b>${esc(title)}</b><small>${esc(f.theme || '')}${f.theme ? ' · ' : ''}${fmt(d.t)}${current ? ' · open now' : ''}</small></span>
        <span class="pb-ai__design-act">
          ${current ? '' : `<button type="button" class="pb-ai-pill pb-ai-pill--out pb-ai-pill--xs" data-open-design="${esc(d.id)}">Open</button>`}
          <button type="button" class="pb-ai__textbtn" data-del-design="${esc(d.id)}">Delete</button>
        </span></li>`;
    }).join('') : '<li class="pb-ai__design-empty">Your designs show up here after our AI draws your first pieces.</li>';
  }
  function openDesigns() {
    renderDesignsList();
    const dlg = $('[data-pb-ai-designs-dialog]');
    if (typeof dlg.showModal === 'function') dlg.showModal();
  }
  async function startNew() {
    const saved = await saveToDesigns();
    if (CFG.template) {
      // On a template product, "New design" means this print again, fresh.
      try {
        await loadTemplate();
        relayout(true);
        writeForm();
        idb.set(DRAFT_KEY, snapshot());
        go(S.step);
        if (saved) alertMsg('Your last design is saved. Find it any time under <b>Your designs</b>.', 'ok');
        return;
      } catch (e) { /* fall back to a blank design */ }
    }
    Object.assign(S, { tpl: null, designId: newId(), step: 1, maxStep: 1, pieces: [], batchId: null, bg: '#ffffff', layout: 'Horizontal Brick',
      sizeV: 5, spaceV: 7, rot: 0, qty: 1, added: false, view: 'print',
      form: { occasion: '', name: '', theme: '', message: '', artStyle: S.form.artStyle, mode: 'mixed' } });
    relayout(true);
    writeForm();
    idb.set(DRAFT_KEY, snapshot());
    go(1);
    if (saved) alertMsg('Your last design is saved. Find it any time under <b>Your designs</b>.', 'ok');
  }
  async function openDesign(id) {
    const d = designs.find((x) => x.id === id);
    if (!d) return;
    await saveToDesigns();
    applyDraft(d);
    relayout(true);
    writeForm();
    idb.set(DRAFT_KEY, snapshot());
    $('[data-pb-ai-designs-dialog]').close();
    go(S.step);
  }

  // ------------------------------------------------------------ Supabase auth
  const authHeaders = (token) => {
    const h = { 'Content-Type': 'application/json' };
    if (KEY) h.apikey = KEY;
    if (token || KEY) h.Authorization = 'Bearer ' + (token || KEY);
    return h;
  };

  function storeSession(s) {
    if (!s || !s.access_token) return null;
    const exp = s.expires_at || Math.floor(Date.now() / 1000) + (s.expires_in || 3600);
    session = { access_token: s.access_token, refresh_token: s.refresh_token, expires_at: exp,
      user_id: (s.user && s.user.id) || session && session.user_id, email: (CFG.customer.email || '').toLowerCase() };
    ssSet(SESSION_KEY, session);
    return session;
  }

  async function multipassLogin() {
    // Signed by Liquid (snippets/pb-multipass-bridge.liquid); the function refuses unsigned requests.
    if (!window.pbMultipassParams) throw new Error('sign-in step 1 unavailable');
    const q = await window.pbMultipassParams();
    let r = await fetch(`${SB}/functions/v1/generate-multipass-token?${q}`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('sign-in step 1 failed (' + r.status + ')');
    const { token } = await r.json();
    r = await fetch(`${SB}/functions/v1/shopify-multipass?token=${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('sign-in step 2 failed (' + r.status + ')');
    const otp = await r.json();
    if (!KEY) throw new Error('missing-key');
    r = await fetch(`${SB}/auth/v1/verify`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ type: otp.type || 'magiclink', token_hash: otp.token }) });
    if (!r.ok) throw new Error('sign-in step 3 failed (' + r.status + ')');
    return storeSession(await r.json());
  }

  async function refreshSession(rt) {
    const r = await fetch(`${SB}/auth/v1/token?grant_type=refresh_token`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ refresh_token: rt }) });
    if (!r.ok) throw new Error('refresh failed');
    return storeSession(await r.json());
  }

  let sessionPromise = null;
  function getSession(force) {
    if (!CFG.customer) return Promise.resolve(null);
    if (!force && session && session.expires_at * 1000 > Date.now() + 60000) return Promise.resolve(session);
    if (sessionPromise) return sessionPromise;
    sessionPromise = (async () => {
      const saved = ssGet(SESSION_KEY);
      if (!force && saved && saved.email === (CFG.customer.email || '').toLowerCase()) {
        session = saved;
        if (saved.expires_at * 1000 > Date.now() + 60000) return saved;
        if (saved.refresh_token) { try { return await refreshSession(saved.refresh_token); } catch (e) { /* fall through */ } }
      }
      return multipassLogin();
    })().finally(() => { sessionPromise = null; });
    return sessionPromise;
  }

  async function fn(name, body, { auth = true } = {}) {
    const s = auth ? await getSession() : null;
    const r = await fetch(`${SB}/functions/v1/${name}`, { method: 'POST', headers: authHeaders(s && s.access_token), body: JSON.stringify(body || {}) });
    let data = null;
    try { data = await r.json(); } catch (e) { /* empty */ }
    return { ok: r.ok, status: r.status, data };
  }

  async function loadCredits(sync) {
    if (!CFG.customer || !KEY) return;
    try {
      if (sync) {
        const r = await fn('sync-user-credits', {});
        if (r.ok && r.data && typeof r.data.balance === 'number') { setCredits(r.data.balance); return; }
      }
      const s = await getSession();
      if (!s || !s.user_id) return;
      const r = await fetch(`${SB}/rest/v1/user_credits?select=balance&user_id=eq.${s.user_id}`, { headers: authHeaders(s.access_token) });
      const rows = r.ok ? await r.json() : [];
      if (rows[0]) setCredits(rows[0].balance);
    } catch (e) { /* credits are informational */ }
  }

  function setCredits(n) {
    S.credits = n;
    const el = $('[data-pb-ai-credits]');
    el.hidden = false;
    $('[data-pb-ai-credits-n]').textContent = Number(n).toLocaleString('en-US');
    $('[data-pb-ai-buy]').hidden = !(CFG.creditPacks || []).length;
  }

  async function loadStyles() {
    if (!KEY) return renderStyles();
    try {
      const r = await fetch(`${SB}/rest/v1/art_styles?select=slug,label,art_style_samples(image_url,subject_name,generation_mode)&is_active=eq.true&order=sort_order.asc`, { headers: authHeaders() });
      if (r.ok) {
        const rows = await r.json();
        // Sentence-case the admin labels; never show a brand name as a style.
        const rename = { pixar: '3D cartoon', 'vintage-christmas-illustration': 'Vintage Christmas', 'watercolor-clip-art': 'Watercolor clip art' };
        const tidy = (l) => { const t = String(l).trim().toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); };
        // One small sample per style: prefer the Balanced (gpt-image-1) Santa, served through the image transformer.
        const sample = (list) => {
          const l = list || [];
          const pick = l.find((x) => x.generation_mode === 'balanced' && x.subject_name === 'santa_clause')
            || l.find((x) => x.subject_name === 'santa_clause') || l[0];
          return pick ? pick.image_url.split('?')[0].replace('/object/public/', '/render/image/public/') + '?width=240&height=240&resize=contain' : '';
        };
        if (Array.isArray(rows) && rows.length) styles = rows.filter((x) => x.slug && x.label).map((x) => ({ slug: x.slug, label: rename[x.slug] || tidy(x.label), img: sample(x.art_style_samples) }));
      }
    } catch (e) { /* keep fallback */ }
    if (pendingStyle && styles.some((x) => x.slug === pendingStyle)) { S.form.artStyle = pendingStyle; pendingStyle = null; }
    if (!styles.some((x) => x.slug === S.form.artStyle)) S.form.artStyle = styles[0].slug;
    renderStyles();
  }

  // ----------------------------------------------- pattern engine (port 1:1)
  // Ported from ~/paperbloom/src/lib/patternTemplates.ts so prints match the app.
  function adjacencyScore(dist, index, asset, cols) {
    const row = Math.floor(index / cols);
    let score = 0;
    const n = [[index - cols, 3], [index + 1, 3], [index + cols, 3], [index - 1, 3],
      [index - cols - 1, 1], [index - cols + 1, 1], [index + cols - 1, 1], [index + cols + 1, 1]];
    for (const [i, w] of n) {
      if (i < 0 || i >= dist.length) continue;
      if (Math.abs(Math.floor(i / cols) - row) > 1) continue;
      if (dist[i] === asset && asset !== -1) score += w;
    }
    return score;
  }

  function distribute(total, count, cols) {
    const dist = new Array(total).fill(-1);
    const pool = [];
    const times = Math.ceil(total / count);
    for (let t = 0; t < times; t++) for (let a = 0; a < count; a++) if (pool.length < total) pool.push(a);
    for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
    for (let pos = 0; pos < total; pos++) {
      let best = -1, bestScore = -Infinity;
      for (const cand of new Set(pool)) {
        const avail = pool.filter((a) => a === cand).length;
        const score = -adjacencyScore(dist, pos, cand, cols) * 100 + avail;
        if (score > bestScore) { bestScore = score; best = cand; }
      }
      dist[pos] = best;
      pool.splice(pool.indexOf(best), 1);
    }
    return dist;
  }

  const TEMPLATES = {
    'Grid': (n, d) => {
      const m = 3.0 * d, cols = Math.ceil(Math.sqrt(n * m)), rows = Math.ceil(n * m / cols);
      const dist = distribute(rows * cols, n, cols), out = [];
      let i = 0;
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) out.push({ x: (c + 0.5) / cols, y: (r + 0.5) / rows, scale: 1, rotation: 0, groupId: dist[i++ % dist.length] });
      return out;
    },
    'Horizontal Brick': (n, d, rot = 0) => {
      const m = 4.0 * d, rows = Math.ceil(Math.sqrt(n * m)), cpr = Math.ceil(n * m / rows);
      const dist = distribute(rows * cpr, n, cpr), out = [];
      let i = 0;
      for (let r = 0; r < rows; r++) {
        const off = r % 2 === 0 ? 0 : 0.5 / cpr;
        for (let c = 0; c < cpr; c++) out.push({ x: ((c + 0.5) / cpr + off) % 1, y: (r + 0.5) / rows, scale: 1, rotation: rot, groupId: dist[i++ % dist.length] });
      }
      return out;
    },
    'Diagonal Brick': (n, d) => TEMPLATES['Horizontal Brick'](n, d, 45),
    'Vertical Brick': (n, d) => {
      const m = 4.0 * d, cols = Math.ceil(Math.sqrt(n * m)), rpc = Math.ceil(n * m / cols);
      const dist = distribute(cols * rpc, n, cols), out = [];
      let i = 0;
      for (let c = 0; c < cols; c++) {
        const off = c % 2 === 0 ? 0 : 0.5 / rpc;
        for (let r = 0; r < rpc; r++) out.push({ x: (c + 0.5) / cols, y: ((r + 0.5) / rpc + off) % 1, scale: 1, rotation: 0, groupId: dist[i++ % dist.length] });
      }
      return out;
    },
    'Checkered': (n, d) => {
      const size = Math.ceil(Math.sqrt(n * 5.0 * d));
      const dist = distribute(size * size, n, size), out = [];
      let i = 0;
      for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) out.push({ x: (c + 0.5) / size, y: (r + 0.5) / size, scale: 1, rotation: 0, groupId: dist[i++ % dist.length] });
      return out;
    },
    'Diagonal Grid': (n, d) => TEMPLATES['Grid'](n, d).map((p) => ({ ...p, rotation: 45 })),
    'Scattered': (n, d, _r, meta) => {
      const m = 5.0 * d, cols = Math.ceil(Math.sqrt(n * m)), rows = Math.ceil(n * m / cols);
      const dist = distribute(rows * cols, n, cols), out = [];
      let i = 0;
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        const g = dist[i++ % dist.length];
        const range = meta[g] && meta[g].isText ? 10 : 25;
        out.push({
          x: (c + 0.5) / cols + ((Math.random() - 0.5) * 0.4) / cols,
          y: (r + 0.5) / rows + ((Math.random() - 0.5) * 0.4) / rows,
          scale: 0.75 + Math.random() * 0.5,
          rotation: -range + Math.random() * range * 2,
          groupId: g,
        });
      }
      return out;
    },
  };

  function toroidal(list, scale, rotation, meta) {
    const wrapped = list.map((p) => ({ ...p, originalY: p.y }));
    list.forEach((p) => {
      const size = BASE * p.scale * scale;
      const rad = (Math.abs(p.rotation + rotation) * Math.PI) / 180;
      const box = size * (Math.abs(Math.cos(rad)) + Math.abs(Math.sin(rad)));
      const th = (box / 2 + 10) / CANVAS;
      let t = Math.min(Math.max(th, th, 0.15), 0.45);
      if (meta[p.groupId] && meta[p.groupId].isText) t = Math.min(t * 1.5, 0.55);
      const add = (dx, dy) => wrapped.push({ ...p, x: p.x + dx, y: p.y + dy, originalY: p.y });
      if (p.x < t) add(1, 0);
      if (p.x > 1 - t) add(-1, 0);
      if (p.y < t) add(0, 1);
      if (p.y > 1 - t) add(0, -1);
      if (p.x < t && p.y < t) add(1, 1);
      if (p.x > 1 - t && p.y < t) add(-1, 1);
      if (p.x < t && p.y > 1 - t) add(1, -1);
      if (p.x > 1 - t && p.y > 1 - t) add(-1, -1);
    });
    const norm = (v) => { const n = v % 1; return n < 0 ? n + 1 : n; };
    wrapped.sort((a, b) => {
      const ya = a.originalY !== undefined ? a.originalY : norm(a.y);
      const yb = b.originalY !== undefined ? b.originalY : norm(b.y);
      if (Math.abs(ya - yb) > 0.001) return ya - yb;
      return norm(a.x) - norm(b.x);
    });
    return wrapped;
  }

  const picked = () => S.pieces.filter((p) => p.picked);
  const meta = () => picked().map((p) => ({ isText: !!p.isText }));

  function relayout(reshuffle) {
    const list = picked();
    if (!list.length) { raw = null; placements = []; return; }
    if (reshuffle || !raw) {
      const tpl = TEMPLATES[S.layout] || TEMPLATES['Horizontal Brick'];
      raw = tpl(list.length, densOf(S.spaceV), 0, meta()).map((p, i) => ({ ...p, originalIndex: i }));
    }
    placements = toroidal(raw, scaleOf(S.sizeV), S.rot, meta());
  }

  function loadImg(src) {
    if (imgCache.has(src)) return imgCache.get(src);
    const p = new Promise((res, rej) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => res(im);
      im.onerror = () => rej(new Error('image failed'));
      im.src = src;
    });
    imgCache.set(src, p);
    p.catch(() => imgCache.delete(src));
    return p;
  }

  // Draws the tile at `k` output pixels per canvas unit. Matches fabric: uniform scale
  // BASE / max(w,h) * placement.scale * userScale, centered, rotated by template + user angle.
  async function paint(ctx, k, list = picked(), pls = placements) {
    const imgs = await Promise.all(list.map((p) => loadImg(p.src).catch(() => null)));
    const scale = scaleOf(S.sizeV);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = S.bg;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    for (const pl of pls) {
      const im = imgs[pl.groupId];
      if (!im) continue;
      const w = im.naturalWidth, h = im.naturalHeight;
      const s = (BASE / Math.max(w, h)) * pl.scale * scale;
      ctx.setTransform(k, 0, 0, k, 0, 0);
      ctx.translate(pl.x * CANVAS, pl.y * CANVAS);
      ctx.rotate(((pl.rotation + S.rot) * Math.PI) / 180);
      ctx.drawImage(im, (-w * s) / 2, (-h * s) / 2, w * s, h * s);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  // ------------------------------------------------------------------ preview
  const canvas = $('[data-pb-ai-canvas]');
  const tileCanvas = document.createElement('canvas');
  let drawQueued = false;
  function draw() {
    if (drawQueued) return;
    drawQueued = true;
    requestAnimationFrame(async () => {
      drawQueued = false;
      const has = placements.length > 0;
      root.classList.toggle('has-pattern', has);
      $('[data-pb-ai-tools]').hidden = !has || S.view !== 'print';
      $('[data-pb-ai-thumbs]').hidden = !has;
      if (!has) { if (S.view !== 'print') setView('print'); return; }
      const css = canvas.getBoundingClientRect().width || 600;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const px = Math.round(css * dpr);
      if (!S.zoom) {
        if (canvas.width !== px) { canvas.width = px; canvas.height = px; }
        await paint(canvas.getContext('2d'), px / CANVAS);
      } else {
        // "See it repeat": 2x2 tiles so shoppers can check the seams.
        const half = Math.round(px / 2);
        if (tileCanvas.width !== half) { tileCanvas.width = half; tileCanvas.height = half; }
        await paint(tileCanvas.getContext('2d'), half / CANVAS);
        if (canvas.width !== px) { canvas.width = px; canvas.height = px; }
        const c = canvas.getContext('2d');
        for (let x = 0; x < 2; x++) for (let y = 0; y < 2; y++) c.drawImage(tileCanvas, x * half, y * half);
      }
      const pt = $('[data-pb-ai-printthumb]');
      if (pt) pt.getContext('2d').drawImage(canvas, 0, 0, pt.width, pt.height);
      refreshLive();
    });
  }

  // ------------------------------------------------------------ live mockups
  // assets/pb-live-mockups.js renders the 5 product scenes from one repeat tile (20in, like the
  // customizer). Its source is a dedicated 1024px tile so "See it repeat" never leaks into it.
  const liveTile = document.createElement('canvas');
  liveTile.width = liveTile.height = LIVE_PX;
  const liveThumbs = $$('[data-pb-live-thumb]');
  const liveStage = $('[data-pb-live-stage]');
  let liveOn = false, liveTimer = null;
  function refreshLive() {
    clearTimeout(liveTimer);
    liveTimer = setTimeout(async () => {
      if (!placements.length || !window.PBLive) return;
      await paint(liveTile.getContext('2d'), LIVE_PX / CANVAS);
      if (!liveOn) {
        liveOn = window.PBLive.mount(liveThumbs, liveTile, { repeatIn: 20 / PRINT_ACROSS });
        root.classList.toggle('has-live', !!liveOn);
      }
      if (liveOn) window.PBLive.update();
    }, 120);
  }
  function setView(v) {
    if (v !== 'print' && !liveOn) v = 'print';
    S.view = v;
    $$('[data-pb-ai-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.pbAiView === v)));
    $('[data-pb-ai-stage]').classList.toggle('is-livemock', v !== 'print');
    $('[data-pb-ai-tools]').hidden = !placements.length || v !== 'print';
    if (!window.PBLive || !liveStage) return;
    if (v === 'print') window.PBLive.remove(liveStage);
    else { window.PBLive.remove(liveStage); window.PBLive.add(liveStage, v); window.PBLive.update(); }
  }
  const stage = $('[data-pb-ai-stage]');
  const desktop = window.matchMedia('(min-width: 900px)');
  new ResizeObserver(() => {
    // Desktop: the step panel is exactly as tall as the square preview.
    root.style.setProperty('--pb-ai-panel-h', desktop.matches ? stage.offsetHeight + 'px' : 'auto');
    // Mobile: the pinned preview sits just under the sticky site header.
    const header = document.querySelector('.pb-header-section');
    root.style.setProperty('--pb-ai-sticky-top', ((header && header.offsetHeight) || 0) + 8 + 'px');
    draw();
  }).observe(stage);

  // ------------------------------------------------------------ colour picks
  // Frequency-ranked colours from the picked pieces (stand-in for ColorThief in the app).
  async function extractPalette() {
    const list = picked();
    const counts = new Map();
    const c = document.createElement('canvas');
    c.width = c.height = 48;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    for (const p of list) {
      let im;
      try { im = await loadImg(p.src); } catch (e) { continue; }
      ctx.clearRect(0, 0, 48, 48);
      ctx.drawImage(im, 0, 0, 48, 48);
      const d = ctx.getImageData(0, 0, 48, 48).data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 200) continue;
        const key = ((d[i] >> 4) << 8) | ((d[i + 1] >> 4) << 4) | (d[i + 2] >> 4);
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    const hex = (k) => '#' + [(k >> 8) & 15, (k >> 4) & 15, k & 15].map((v) => (v * 16 + 8).toString(16).padStart(2, '0')).join('');
    const dist = (a, b) => { const p = (h, o) => parseInt(h.substr(o, 2), 16); return Math.hypot(p(a, 1) - p(b, 1), p(a, 3) - p(b, 3), p(a, 5) - p(b, 5)); };
    const out = [];
    [...counts.entries()].sort((a, b) => b[1] - a[1]).forEach(([k]) => {
      const h = hex(k);
      if (out.length < 9 && out.every((x) => dist(x, h) > 60)) out.push(h);
    });
    palette = out;
  }

  // ----------------------------------------------------------------- render UI
  function renderStyles() {
    const box = $('[data-pb-ai-styles]');
    let shown = S.stylesOpen ? styles : styles.slice(0, STYLES_SHOWN);
    const cur = styles.find((x) => x.slug === S.form.artStyle);
    if (cur && !shown.includes(cur)) shown = shown.slice(0, STYLES_SHOWN - 1).concat(cur);
    box.innerHTML = shown.map((st) => `<button type="button" class="pb-ai__style" role="radio" aria-checked="${st.slug === S.form.artStyle}" data-style="${esc(st.slug)}">
        <span class="pb-ai__style-img">${st.img ? `<img src="${esc(st.img)}" alt="" loading="lazy" decoding="async" width="120" height="120">` : ''}</span>
        <span class="pb-ai__style-name">${esc(st.label)}</span></button>`).join('');
    const more = $('[data-pb-ai-styles-more]');
    more.hidden = styles.length <= STYLES_SHOWN;
    more.textContent = S.stylesOpen ? 'Fewer styles' : `All ${styles.length} styles`;
    more.setAttribute('aria-expanded', String(S.stylesOpen));
  }

  function renderPieces() {
    const box = $('[data-pb-ai-pieces]');
    const pending = S.busy ? Array.from({ length: S.form.mode === 'text-only' ? 10 : 10 }) : [];
    const items = S.pieces.map((p, i) => {
      const busy = S.busyPieces.has(p.uid);
      const label = p.kind === 'text' ? 'Your text' : p.kind === 'photo' ? 'Your photo' : 'Piece ' + (i + 1);
      const redraw = p.kind === 'ai' ? `<button type="button" class="pb-ai__piece-act" data-redraw="${p.uid}" aria-label="Redraw ${esc(label)} (1 credit)" title="Redraw (1 credit)"${busy || S.busy ? ' disabled' : ''}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M15.5 8A6 6 0 1 0 16 12M16 3.5V8h-4.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`
        : `<button type="button" class="pb-ai__piece-act" data-remove="${p.uid}" aria-label="Remove ${esc(label)}" title="Remove"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg></button>`;
      return `<div class="pb-ai__piece${p.picked ? ' is-on' : ''}${busy ? ' is-busy' : ''}">
        <button type="button" class="pb-ai__piece-btn" data-toggle="${p.uid}" aria-pressed="${p.picked}" aria-label="${esc(label)}">
          <img src="${esc(p.src)}" alt="" loading="lazy" decoding="async">
        </button>
        <span class="pb-ai__piece-check" aria-hidden="true">${p.picked ? '✓' : ''}</span>
        ${redraw}
      </div>`;
    });
    box.innerHTML = items.join('') + pending.map(() => '<div class="pb-ai__piece is-skeleton" aria-hidden="true"></div>').join('');
    const n = picked().length;
    const ai = S.pieces.filter((p) => p.kind === 'ai').length;
    $('[data-pb-ai-pieces-sub]').textContent = S.busy
      ? 'Our AI is drawing your pieces. This usually takes a minute or two.'
      : ai ? `Our AI drew ${ai} pieces for you. Tap the ones you want on your print. ${n} picked.`
        : `Tap the pieces you want on your print. ${n} picked.`;
    $('[data-pb-ai-more-cost]').textContent = `${CFG.creditsPerSet} credits`;
    $('[data-pb-ai-draw-more]').disabled = S.busy;
  }

  function renderColors() {
    const box = $('[data-pb-ai-colors]');
    const base = ['#ffffff', '#FDF3EA', '#F7DED2', '#FBA3B5', '#CFE4EA'];
    const all = [];
    // The shopper's own picks (and a template's own background) sit in the row as normal swatches, and the
    // picker stays an "Add a color" button, so it always reads as "tap to add another".
    [...base, ...palette, ...S.myBgs, S.bg].forEach((c) => { if (c && !all.some((x) => x.toLowerCase() === c.toLowerCase())) all.push(c); });
    box.innerHTML = all.map((c) => `<button type="button" class="pb-ai__swatch" style="--c:${c}" data-bg="${c}" aria-pressed="${c.toLowerCase() === S.bg.toLowerCase()}" aria-label="Background ${c}"></button>`).join('') +
      `<label class="pb-ai__swatch-any"><span class="pb-ai__swatch-dot" style="--c:conic-gradient(#E4553C,#E0A526,#2E6B45,#2F6F8F,#F78BA1,#E4553C)"></span>Add a color
       <input type="color" value="${S.bg.length === 7 ? S.bg : '#ffffff'}" data-bg-any aria-label="Add a background color"></label>`;
  }

  function renderLayouts() {
    const list = LAYOUTS.some((l) => l.id === S.layout) ? LAYOUTS
      : LAYOUTS.concat({ id: S.layout, label: 'Original', dots: [[4, 4, 0], [18, 4, 0], [32, 4, 0], [11, 18, 0], [25, 18, 0], [4, 32, 0], [18, 32, 0], [32, 32, 0]] });
    $('[data-pb-ai-layouts]').innerHTML = list.map((l) => `<button type="button" class="pb-ai__layout" data-layout="${l.id}" aria-pressed="${l.id === S.layout}">
      <span class="pb-ai__layout-ico" aria-hidden="true">${l.dots.map((d) => `<i style="left:${d[0]}px;top:${d[1]}px;transform:rotate(${d[2]}deg)"></i>`).join('')}</span>${l.label}</button>`).join('');
  }

  function variants() { return (CFG.product && CFG.product.variants) || []; }
  function currentVariant() { return variants().find((v) => v.title === S.roll) || variants()[0]; }

  function renderRolls() {
    if (!S.roll && variants().length) S.roll = variants()[0].title;
    const sale = CFG.sale;
    $('[data-pb-ai-rolls]').innerHTML = variants().map((v) => {
      const now = sale ? Math.round(v.price * (100 - sale.percent) / 100) : v.price;
      return `<button type="button" class="pb-ai__roll" data-roll="${esc(v.title)}" aria-pressed="${v.title === S.roll}"${v.available ? '' : ' disabled'}>
        <span class="pb-ai__roll-size">${esc(v.title.replace(/ft$/i, ' ft'))}</span>
        <span class="pb-ai__roll-price">${sale ? `<span class="pb-price__now--sale">${money(now)}</span> <s>${money(v.price)}</s>` : money(v.price)}</span>
      </button>`;
    }).join('');
    $('[data-pb-ai-qty-n]').textContent = S.qty;
    const v = currentVariant();
    if (!v) return;
    const sub = v.price * S.qty;
    let html = `<div class="pb-ai__sum-row"><span>Subtotal</span><span>${money(sub)}</span></div>`;
    if (sale) {
      const off = Math.round(sub * sale.percent / 100);
      html += `<div class="pb-ai__sum-row pb-ai__sum-sale"><span>${sale.percent}% off${sale.code ? ` with code ${esc(sale.code)}` : ''}</span><span>&minus;${money(off)}</span></div>
        <div class="pb-ai__sum-row pb-ai__sum-total"><span>Estimated total</span><span class="pb-display">${money(sub - off)}</span></div>
        <p class="pb-ai__hint">${sale.code ? 'Enter the code at checkout.' : 'Discount applies at checkout.'}</p>`;
    } else {
      html += `<div class="pb-ai__sum-row pb-ai__sum-total"><span>Total</span><span class="pb-display">${money(sub)}</span></div>`;
    }
    $('[data-pb-ai-summary]').innerHTML = html;
  }

  function renderSliders() {
    $('[data-pb-ai-size]').value = S.sizeV;
    $('[data-pb-ai-space]').value = S.spaceV;
    $('[data-pb-ai-rot]').value = S.rot;
    $('[data-pb-ai-size-l]').textContent = ['Tiny', 'Tiny', 'Small', 'Small', 'Medium', 'Medium', 'Large', 'Large', 'Huge', 'Huge'][S.sizeV - 1];
    $('[data-pb-ai-space-l]').textContent = S.spaceV <= 3 ? 'Packed' : S.spaceV <= 7 ? 'Balanced' : 'Airy';
    $('[data-pb-ai-rot-l]').textContent = S.rot === 0 ? 'Straight' : `${S.rot}°`;
  }

  function renderSteps() {
    root.dataset.step = S.step;
    $$('[data-pb-ai-pane]').forEach((p) => { p.hidden = Number(p.dataset.pbAiPane) !== S.step; });
    $$('[data-pb-ai-go]').forEach((b) => {
      const n = Number(b.dataset.pbAiGo);
      b.disabled = n > S.maxStep || S.busy;
      b.classList.toggle('is-done', n < S.step);
      b.classList.toggle('is-reach', n <= S.maxStep);
      if (n === S.step) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
    });
    const back = $('[data-pb-ai-back]');
    const next = $('[data-pb-ai-next]');
    back.hidden = S.step === 1 || S.busy;
    next.disabled = false;
    next.classList.remove('is-loading');
    const n = picked().length;
    if (S.step === 1) {
      if (S.busy) { next.textContent = 'Drawing your pieces…'; next.disabled = true; next.classList.add('is-loading'); }
      else if (S.pieces.some((p) => p.kind === 'ai')) next.textContent = 'Next: choose pieces';
      else next.textContent = CFG.customer ? `Create my pieces · ${CFG.creditsPerSet} credits` : 'Sign in to create';
    } else if (S.step === 2) {
      next.textContent = S.busy ? 'Drawing your pieces…' : n ? `Next: customize (${n} picked)` : 'Pick at least one piece';
      next.disabled = S.busy || !n;
    } else if (S.step === 3) {
      next.textContent = 'Next: roll size';
    } else {
      const v = currentVariant();
      const total = v ? v.price * S.qty : 0;
      next.textContent = S.added ? 'Added to cart' : `Add to cart · ${money(total)}`;
      next.disabled = S.added;
    }
    $('[data-pb-ai-reset]').hidden = !S.pieces.length || S.busy;
    $('[data-pb-ai-empty]').hidden = placements.length > 0 || S.busy;
    $('[data-pb-ai-done]').hidden = !S.added;
  }

  function renderAll() {
    renderSteps();
    renderShareBox();
    renderTemplateName();
    renderPieces();
    renderColors();
    renderLayouts();
    renderRolls();
    renderSliders();
    draw();
  }

  function go(step) {
    S.step = Math.max(1, Math.min(4, step));
    S.maxStep = Math.max(S.maxStep, S.step);
    alertMsg('');
    if (S.step === 3) extractPalette().then(renderColors);
    renderAll();
    saveDraft();
    if (window.matchMedia('(max-width: 899px)').matches) {
      const target = S.step === 1 ? root : $('.pb-ai__panel');
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      $('[data-pb-ai-body]').scrollTop = 0;
    }
  }

  // ------------------------------------------------------- start from template
  // The "start from template" products (template suffix start-from-template) preload their saved
  // AI pattern like the Lovable app's SHOPIFY_LOAD_TEMPLATE: pattern_templates row by Shopify
  // product id (then handle), its source saved_pattern for versions/hidden pieces, and the
  // generated_assets files. The shopper lands on Customize with the name editable.
  const rest = (path, token) => fetch(`${SB}/rest/v1/${path}`, { headers: authHeaders(token) }).then((r) => (r.ok ? r.json() : []));
  const nearest = (fnOf, target) => { let best = 1; for (let v = 1; v <= 10; v++) if (Math.abs(fnOf(v) - target) < Math.abs(fnOf(best) - target)) best = v; return best; };
  function markEdited() { if (S.tpl) S.tpl.edited = true; S.added = false; } // any design change allows adding the new version

  async function loadTemplate() {
    const T = CFG.template;
    const cols = 'id,name,batch_id,theme,occasion,art_style,template_layout,background_color,density,scale,rotation,asset_ids,hidden_assets,customizable_slots,source_saved_pattern_id';
    let rows = await rest(`pattern_templates?select=${cols}&status=eq.published&shopify_product_id=eq.${encodeURIComponent(T.productId)}&limit=1`);
    if (!rows.length) rows = await rest(`pattern_templates?select=${cols}&status=eq.published&shopify_product_handle=eq.${encodeURIComponent(T.handle)}&limit=1`);
    const t = rows[0];
    if (!t) throw new Error('no template');
    let src = t;
    if (t.source_saved_pattern_id) {
      const sp = await rest(`saved_patterns?select=density,scale,rotation,background_color,hidden_assets,selected_asset_versions,asset_version_map,template_name&id=eq.${t.source_saved_pattern_id}`);
      if (sp[0]) src = Object.assign({}, t, sp[0]);
    }
    // Some saved patterns carry an empty version map ({}): that is "no versions recorded", not "no pieces", so the
    // template's own asset list is used (it was showing an empty print on those products).
    const vmap = src.asset_version_map && Object.keys(src.asset_version_map).length ? src.asset_version_map : null;
    const map = vmap || Object.fromEntries((t.asset_ids || []).map((id, i) => [i, [id]]));
    const chosen = src.selected_asset_versions || {};
    const hidden = new Set(src.hidden_assets || []);
    const ids = Object.values(map).flat();
    const assets = ids.length ? await rest(`generated_assets?select=id,file_url,asset_metadata,generation_prompt,name&id=in.(${ids.join(',')})`) : [];
    const byId = new Map(assets.map((a) => [a.id, a]));
    const pieces = [];
    Object.keys(map).sort((a, b) => a - b).forEach((slot) => {
      const sel = Array.isArray(chosen[slot]) && chosen[slot].length ? chosen[slot] : [0];
      map[slot].forEach((assetId, v) => {
        const a = byId.get(assetId);
        if (!a || !a.file_url) return;
        pieces.push({ uid: uid(), kind: 'ai', src: a.file_url, prompt: a.generation_prompt || null, isText: !!(a.asset_metadata && a.asset_metadata.isText),
          assetId, slot: Number(slot), picked: sel.includes(v) && !hidden.has(`${slot}-${v}`) });
      });
    });
    const rawSlots = (t.customizable_slots && t.customizable_slots.slots) || t.customizable_slots || [];
    const slots = rawSlots.filter((x) => x.type === 'personalization' && x.assetId);
    const quoted = slots.map((x) => (String(x.promptTemplate || x.description || '').match(/["'“‘]([^"'”’]+)["'”’]/) || [])[1]).find(Boolean);
    const firstName = slots.length && byId.get(slots[0].assetId);
    S.tpl = { id: t.id, title: T.title, layout: src.template_name || t.template_layout, batchAssetIds: t.asset_ids || [],
      slots: slots.map((x) => ({ id: x.id, assetId: x.assetId, cost: Number(x.creditCost) || 2 })),
      currentName: quoted || (firstName && firstName.name) || '', personalized: '', edited: false,
      raw: { name: t.name, theme: t.theme, occasion: t.occasion, art_style: t.art_style, template_layout: t.template_layout,
        background_color: t.background_color, density: t.density, scale: t.scale, rotation: t.rotation, asset_ids: t.asset_ids,
        batch_id: t.batch_id } };
    Object.assign(S, {
      designId: newId(), pieces, batchId: null, step: 3, maxStep: 4, added: false, view: 'print',
      form: { occasion: t.occasion || '', name: '', theme: t.theme || '', message: '', artStyle: t.art_style || S.form.artStyle, mode: 'mixed' },
      bg: src.background_color || '#ffffff', myBgs: src.background_color ? [src.background_color] : [], layout: src.template_name || t.template_layout || 'Horizontal Brick',
      sizeV: nearest(scaleOf, Number(src.scale) || 2.5), spaceV: nearest(densOf, 3.3 - (Number(src.density) || 2.5)), rot: Number(src.rotation) || 0,
    });
    markNameTwins();
    dropBoxedPieces();
  }

  // A template can carry a piece whose background was never removed: it prints as a picture box sitting on the
  // pattern (seen on the dirt bike Santa print). Such a piece starts switched off; the shopper can still turn
  // it on from the pieces list.
  async function dropBoxedPieces() {
    let changed = false;
    await Promise.all(S.pieces.filter((p) => p.picked && !p.isText).map(async (p) => {
      try {
        const im = await loadImg(p.src);
        const c = document.createElement('canvas');
        c.width = c.height = 24;
        const x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(im, 0, 0, 24, 24);
        const d = x.getImageData(0, 0, 24, 24).data;
        const a = (px, py) => d[(py * 24 + px) * 4 + 3];
        if ([a(0, 0), a(23, 0), a(0, 23), a(23, 23)].filter((v) => v > 250).length >= 3) { p.picked = false; changed = true; }
      } catch (e) { /* unreadable pieces are handled where they are drawn */ }
    }));
    if (changed) { relayout(true); renderAll(); }
  }

  // Some templates draw the name in more than one lettering piece but only mark one as the editable slot.
  // Those extra pieces ("name twins") can't be redrawn by the studio, so when the shopper adds their name they
  // take the redrawn slot's lettering. Otherwise the print would show the new name next to the sample one.
  function markNameTwins() {
    const T = S.tpl;
    if (!T || !T.slots.length || !T.currentName) return;
    const named = new RegExp(`["'“‘]${T.currentName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'”’]`, 'i');
    const slotIds = new Set(T.slots.map((x) => x.assetId));
    S.pieces.forEach((p) => {
      if (p.isText && !slotIds.has(p.assetId) && !slotIds.has(p.origAssetId) && named.test(p.prompt || '')) p.nameTwin = true;
    });
    // A design saved before this existed: the name was already redrawn, so the twins catch up now.
    const done = T.personalized && S.pieces.find((p) => slotIds.has(p.origAssetId));
    if (done) S.pieces.forEach((p) => { if (p.nameTwin && p.src !== done.src) { p.assetId = done.assetId; p.src = done.src; } });
  }

  function renderTemplateName() {
    const box = $('[data-pb-ai-tname]');
    if (!box) return;
    const on = !!(S.tpl && S.tpl.slots.length);
    box.hidden = !on;
    if (!on) return;
    const cost = S.tpl.slots.reduce((n, x) => n + x.cost, 0);
    const input = $('#pb-ai-tname-in');
    if (document.activeElement !== input) input.value = S.form.name || '';
    input.placeholder = S.tpl.currentName ? `Instead of "${S.tpl.currentName}"` : 'Their name';
    $('[data-pb-ai-tname-go]').textContent = CFG.customer ? `Put their name on it · ${cost} credits` : 'Sign in to add their name';
    $('[data-pb-ai-tname-note]').textContent = S.tpl.personalized
      ? `Showing "${S.tpl.personalized}". Change it any time.`
      : `Our AI redraws the lettering${S.tpl.slots.length > 1 ? ` in all ${S.tpl.slots.length} name pieces` : ''} in the same style, everywhere their name appears. It takes about half a minute.`;
  }

  async function personalize() {
    if (!CFG.customer) return sendToLogin();
    const input = $('#pb-ai-tname-in');
    const name = input.value.trim();
    if (!name) { input.focus(); input.setAttribute('aria-invalid', 'true'); return; }
    input.removeAttribute('aria-invalid');
    S.form.name = name;
    const btn = $('[data-pb-ai-tname-go]');
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = 'Lettering their name…';
    alertMsg('');
    const busy = S.pieces.filter((p) => p.nameTwin || S.tpl.slots.some((x) => x.assetId === p.assetId || x.assetId === p.origAssetId));
    busy.forEach((p) => S.busyPieces.add(p.uid));
    renderPieces();
    try {
      const s = await getSession();
      // A fresh customization row per run, so every slot's original asset is still in current_asset_ids.
      const raw = S.tpl.raw;
      const ins = await fetch(`${SB}/rest/v1/customer_pattern_customizations`, {
        method: 'POST', headers: Object.assign(authHeaders(s.access_token), { Prefer: 'return=representation' }),
        body: JSON.stringify({ user_id: s.user_id, source_type: 'template', pattern_template_id: S.tpl.id, name: raw.name, theme: raw.theme,
          occasion: raw.occasion, art_style: raw.art_style, generation_mode: 'mixed', template_layout: raw.template_layout,
          background_color: raw.background_color, density: raw.density, scale: raw.scale, rotation: raw.rotation,
          base_batch_id: raw.batch_id, current_asset_ids: raw.asset_ids }),
      });
      const row = ins.ok ? (await ins.json())[0] : null;
      if (!row) throw new Error('customization');
      const results = await Promise.all(S.tpl.slots.map((slot) => fn('edit-text-asset', { customizationId: row.id, slotId: slot.id, newText: name })));
      let done = 0, credits = null, lastErr = null;
      results.forEach((r, i) => {
        const url = r.data && r.data.new_asset && r.data.new_asset.file_url;
        if (r.ok && url) {
          done++;
          if (typeof r.data.new_balance === 'number') credits = r.data.new_balance;
          const tid = S.tpl.slots[i].assetId;
          S.pieces.forEach((p) => { if (p.assetId === tid || p.origAssetId === tid) { p.origAssetId = tid; p.assetId = r.data.new_asset.id; p.src = url; } });
          if (done === 1) S.pieces.forEach((p) => { if (p.nameTwin) { p.assetId = r.data.new_asset.id; p.src = url; } });
        } else lastErr = r;
      });
      if (credits != null) setCredits(credits);
      if (done) { S.tpl.personalized = name; markEdited(); relayout(false); saveDraft(); saveToDesigns(); }
      if (lastErr) {
        const e = friendlyError(lastErr);
        if (e.credits) openCredits(true);
        alertMsg(done ? 'Some name pieces could not be redrawn. Try again to finish them.' : (e.credits ? 'You need a few more credits to add their name.' : esc(e.text)));
      }
    } catch (e) {
      alertMsg('We could not reach the studio. Check your connection and try again.');
    }
    busy.forEach((p) => S.busyPieces.delete(p.uid));
    btn.disabled = false;
    btn.classList.remove('is-loading');
    renderAll();
    loadCredits();
  }

  // --------------------------------------------------------------- generation
  let tipTimer = null;
  function setLoading(on) {
    S.busy = on;
    const box = $('[data-pb-ai-loading]');
    box.hidden = !on;
    root.classList.toggle('is-busy', on);
    clearInterval(tipTimer);
    if (on) {
      const t0 = Date.now();
      let i = 0;
      const bar = $('[data-pb-ai-progress]');
      const tip = $('[data-pb-ai-tip]');
      tip.textContent = TIPS[0];
      bar.style.transform = 'scaleX(.03)';
      tipTimer = setInterval(() => {
        const s = (Date.now() - t0) / 1000;
        bar.style.transform = `scaleX(${Math.min(0.96, 1 - Math.exp(-s / 45))})`;
        const idx = Math.min(TIPS.length - 1, Math.floor(s / 12));
        if (idx !== i) { i = idx; tip.textContent = TIPS[i]; }
      }, 500);
    }
  }

  function readForm() {
    const f = $('[data-pb-ai-pane="1"]');
    S.form.occasion = f.occasion.value.trim();
    S.form.name = f.name.value.trim();
    S.form.theme = f.theme.value.trim();
    S.form.message = f.message.value.trim();
    S.form.mode = 'mixed'; // text-only option removed (it confused shoppers); the backend still gets 'mixed'
  }

  function writeForm() {
    const f = $('[data-pb-ai-pane="1"]');
    f.occasion.value = S.form.occasion || '';
    f.name.value = S.form.name || '';
    f.theme.value = S.form.theme || '';
    f.message.value = S.form.message || '';
    if (S.form.message) showMessageField();
    renderStyles();
  }

  function showMessageField() {
    $('[data-pb-ai-msg-field]').hidden = false;
    $('[data-pb-ai-msg-open]').hidden = true;
  }

  function validate() {
    readForm();
    const f = $('[data-pb-ai-pane="1"]');
    for (const k of ['occasion', 'name', 'theme']) {
      if (!S.form[k]) {
        f[k].focus();
        f[k].setAttribute('aria-invalid', 'true');
        alertMsg({ occasion: 'Add the occasion so we know what to draw.', name: 'Add their name. We draw it as one of the pieces.', theme: 'Tell us something they love. It is what the pieces are about.' }[k]);
        return false;
      }
      f[k].removeAttribute('aria-invalid');
    }
    return true;
  }

  function sendToLogin() {
    readForm();
    saveDraft();
    const back = location.pathname + location.search + '#pb-ai-studio';
    location.href = `${CFG.loginUrl}?return_url=${encodeURIComponent(back)}`;
  }

  function friendlyError(r) {
    const msg = (r && r.data && r.data.error) || '';
    if (r && r.status === 402) return { credits: true, text: msg };
    if (/moderation|safety|rejected/i.test(msg)) return { text: 'Our AI could not draw that one. Try different words for what they love.' };
    if (r && r.status === 429) return { text: 'Lots of people are designing right now. Give it a moment and try again.' };
    return { text: 'Something went wrong while drawing your pieces. Your credits are refunded for anything that failed. Please try again.' };
  }

  // SeedDream (Replicate) draws first: the studio's OpenAI key still failed every piece on 2026-10-27, which cost
  // each set ~20 s before falling back. If every piece fails the set is refunded and redrawn with OpenAI,
  // which also backs up single-piece redraws. Swap these back once OpenAI draws again.
  const DRAW_MODEL = 'seeddream';
  const BACKUP_MODEL = 'openai';
  const allFailed = (r) => r.ok && r.data && !r.data.error && !(r.data.asset_ids || []).some(Boolean) && r.data.failed_count > 0;

  // SeedDream sets come back without inline pictures: the function stores them and we read the files.
  async function storedImages(ids) {
    const want = ids.filter(Boolean);
    if (!want.length) return {};
    const s = await getSession();
    const path = `generated_assets?select=id,file_url,processing_status,temp_image_url,asset_metadata,generation_prompt&id=in.(${want.join(',')})`;
    const done = (x) => x.file_url && !x.temp_image_url && x.processing_status === 'completed';
    let rows = [];
    for (let i = 0; i < 8; i++) {
      rows = await rest(path, s && s.access_token);
      if (rows.filter(done).length >= want.length) break;
      if (i === 2) want.forEach((assetId) => fn('finalize-asset-storage', { assetId }).catch(() => {}));
      await new Promise((ok) => setTimeout(ok, 1500));
    }
    // Background removal sometimes never starts (left on pending_bg_removal with the green
    // backdrop) or fails once; ask for it again before giving up on a piece.
    const stuck = want.filter((id) => !rows.some((x) => x.id === id && done(x)));
    if (stuck.length) {
      await Promise.all(stuck.map((assetId) => fn('remove-asset-background', { assetId }).catch(() => null)));
      rows = await rest(path, s && s.access_token);
    }
    const out = {};
    rows.forEach((x) => { if (done(x)) out[x.id] = x; });
    return out;
  }

  async function generate() {
    if (!CFG.customer) return sendToLogin();
    if (!validate()) return;
    if (!KEY) { alertMsg('The studio is not connected yet. (Missing Supabase key in the theme settings.)'); return; }
    alertMsg('');
    setLoading(true);
    if (S.step === 1 && !S.pieces.length) go(2); else renderAll();
    try {
      await getSession();
      const body = { theme: S.form.theme, occasion: S.form.occasion, name: S.form.name, message: S.form.message || undefined,
        artStyle: S.form.artStyle, generationMode: S.form.mode, model: DRAW_MODEL };
      let r = await fn('generate-pattern', body);
      if (r.status === 401) { await getSession(true); await loadCredits(true); r = await fn('generate-pattern', body); }
      if (allFailed(r)) r = await fn('generate-pattern', { ...body, model: BACKUP_MODEL });
      if (!r.ok || !r.data || r.data.error) {
        const e = friendlyError(r);
        setLoading(false);
        if (e.credits) { openCredits(true); alertMsg('You need a few more credits to draw a new set.'); }
        else alertMsg(esc(e.text));
        if (!S.pieces.length) go(1); else renderAll();
        loadCredits();
        return;
      }
      const d = r.data;
      S.batchId = d.batch_id || S.batchId;
      const ids = d.asset_ids || [];
      const images = d.images || [];
      const md = d.metadata || [];
      const prompts = d.prompts || [];
      let k = 0;
      const fresh = [];
      if (images.length) {
        ids.forEach((id, slot) => {
          if (!id || k >= images.length) return;
          fresh.push({ uid: uid(), kind: 'ai', src: images[k], prompt: prompts[slot] || null, isText: !!(md[k] && md[k].isText),
            assetId: id, slot, picked: false });
          k++;
        });
      } else {
        const stored = await storedImages(ids);
        ids.forEach((id, slot) => {
          const row = id && stored[id];
          if (!row) return;
          fresh.push({ uid: uid(), kind: 'ai', src: row.file_url, prompt: row.generation_prompt || null,
            isText: !!(row.asset_metadata && row.asset_metadata.isText), assetId: id, slot, picked: false });
        });
      }
      if (!fresh.length) {
        setLoading(false);
        alertMsg(esc(friendlyError(r).text));
        if (!S.pieces.length) go(1); else renderAll();
        loadCredits();
        return;
      }
      // Pre-pick the first five like the app does (just the lettering in text-only mode).
      const firstSet = !S.pieces.some((p) => p.kind === 'ai');
      if (firstSet) fresh.slice(0, S.form.mode === 'text-only' ? 1 : 5).forEach((p) => { p.picked = true; });
      S.pieces = S.pieces.concat(fresh);
      ids.filter(Boolean).forEach((assetId) => fn('finalize-asset-storage', { assetId }).catch(() => {}));
      setLoading(false);
      if (d.failed_count) alertMsg(`${d.failed_count} piece${d.failed_count > 1 ? 's' : ''} could not be drawn, so we refunded ${d.failed_count} credit${d.failed_count > 1 ? 's' : ''}.`);
      relayout(true);
      if (S.step < 2) go(2); else renderAll();
      saveDraft();
      saveToDesigns();
      loadCredits();
    } catch (e) {
      setLoading(false);
      alertMsg(e && e.message === 'missing-key'
        ? 'The studio is not connected yet. (Missing Supabase key in the theme settings.)'
        : 'We could not reach the studio. Check your connection and try again.');
      if (!S.pieces.length) go(1); else renderAll();
    }
  }

  async function redraw(u) {
    const p = S.pieces.find((x) => x.uid === u);
    if (!p || S.busyPieces.has(u)) return;
    S.busyPieces.add(u);
    renderPieces();
    try {
      const body = { theme: S.form.theme, occasion: S.form.occasion, name: S.form.name, message: S.form.message || undefined,
        artStyle: S.form.artStyle, generationMode: S.form.mode, model: DRAW_MODEL, regenerateIndex: p.slot,
        existingBatchId: S.batchId || undefined };
      if (p.prompt) body.existingPrompt = p.prompt;
      let r = await fn('generate-pattern', body);
      if (r.status >= 500 || (r.ok && !(r.data && r.data.images && r.data.images[0]))) r = await fn('generate-pattern', { ...body, model: BACKUP_MODEL });
      const img = r.data && r.data.images && r.data.images[0];
      if (!r.ok || !img) {
        const e = friendlyError(r);
        if (e.credits) openCredits(true);
        alertMsg(e.credits ? 'You need 1 credit to redraw a piece.' : esc(e.text));
      } else {
        p.src = img;
        p.assetId = r.data.asset_id || (r.data.asset_ids && r.data.asset_ids[0]) || p.assetId;
        if (r.data.prompt) p.prompt = r.data.prompt;
        if (p.assetId) fn('finalize-asset-storage', { assetId: p.assetId }).catch(() => {});
        markEdited();
        relayout(false);
        saveDraft();
      }
    } catch (e) {
      alertMsg('We could not reach the studio. Check your connection and try again.');
    }
    S.busyPieces.delete(u);
    renderAll();
    loadCredits();
  }

  // --------------------------------------------------------- photo + text adds
  function fileToPiece(file) {
    return new Promise((res, rej) => {
      const url = URL.createObjectURL(file);
      const im = new Image();
      im.onload = () => {
        const max = 1024;
        const s = Math.min(1, max / Math.max(im.naturalWidth, im.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.round(im.naturalWidth * s);
        c.height = Math.round(im.naturalHeight * s);
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        URL.revokeObjectURL(url);
        res(c.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.92));
      };
      im.onerror = () => { URL.revokeObjectURL(url); rej(new Error('Could not read that image')); };
      im.src = url;
    });
  }

  let fontsLoaded = null;
  function loadFonts() {
    if (fontsLoaded) return fontsLoaded;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?' + FONTS.map((f) => 'family=' + f.q).join('&') + '&display=swap';
    document.head.appendChild(link);
    fontsLoaded = new Promise((res) => { link.onload = res; link.onerror = res; })
      .then(() => Promise.all(FONTS.map((f) => document.fonts.load(f.css, 'Aa').catch(() => null))));
    return fontsLoaded;
  }

  const texter = { font: FONTS[0].id, ink: INKS[0] };
  function renderTexter() {
    $('[data-pb-ai-fonts]').innerHTML = FONTS.map((f) => `<button type="button" class="pb-ai__font" role="radio" aria-checked="${f.id === texter.font}" data-font="${f.id}" style="font-family:'${f.id}'">${f.label}</button>`).join('');
    $('[data-pb-ai-inks]').innerHTML = INKS.map((c) => `<button type="button" class="pb-ai__swatch pb-ai__swatch--sm" role="radio" aria-checked="${c === texter.ink}" aria-pressed="${c === texter.ink}" data-ink="${c}" style="--c:${c}" aria-label="Color ${c}"></button>`).join('');
  }

  async function textToPiece(text) {
    await loadFonts();
    const f = FONTS.find((x) => x.id === texter.font) || FONTS[0];
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    ctx.font = f.css;
    const m = ctx.measureText(text);
    const asc = m.actualBoundingBoxAscent || 120, desc = m.actualBoundingBoxDescent || 40;
    const pad = 24;
    c.width = Math.ceil(m.width + pad * 2);
    c.height = Math.ceil(asc + desc + pad * 2);
    ctx.font = f.css;
    ctx.fillStyle = texter.ink;
    ctx.textBaseline = 'alphabetic';
    if (texter.ink.toUpperCase() === '#FFFFFF') { ctx.lineWidth = 10; ctx.strokeStyle = '#6B2E2E'; ctx.lineJoin = 'round'; ctx.strokeText(text, pad, pad + asc); }
    ctx.fillText(text, pad, pad + asc);
    return c.toDataURL('image/png');
  }

  function addPiece(p) {
    markEdited();
    S.pieces.push(Object.assign({ uid: uid(), picked: true, prompt: null, assetId: null, slot: null }, p));
    relayout(true);
    renderAll();
    saveDraft();
  }

  // ------------------------------------------------------ community gallery
  // Opt-in only. The shared image is a separate, name-free render: AI pieces only, no lettering,
  // no uploaded photos and no custom text, laid out with the shopper's layout, colors and size.
  const shareable = () => picked().filter((p) => p.kind === 'ai' && !p.isText);
  function renderShareBox() {
    const box = $('[data-pb-ai-share]');
    if (!box) return;
    box.hidden = shareable().length === 0;
  }
  async function shareBlob() {
    const list = shareable();
    if (!list.length) return null;
    const m = list.map((p) => ({ isText: !!p.isText }));
    const tpl = TEMPLATES[S.layout] || TEMPLATES['Horizontal Brick'];
    const base = tpl(list.length, densOf(S.spaceV), 0, m).map((p, i) => ({ ...p, originalIndex: i }));
    const pls = toroidal(base, scaleOf(S.sizeV), S.rot, m);
    const c = document.createElement('canvas');
    c.width = c.height = Math.floor(CANVAS * THUMB_MULT);
    await paint(c.getContext('2d'), THUMB_MULT, list, pls);
    return new Promise((res) => c.toBlob(res, 'image/jpeg', 0.8));
  }

  // ---------------------------------------------------------------- add to cart
  function canvasBlob(k, quality) {
    const c = document.createElement('canvas');
    c.width = c.height = Math.floor(CANVAS * k);
    return paint(c.getContext('2d'), k).then(() => new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('export failed'))), 'image/jpeg', quality)));
  }

  async function upload(name, blob) {
    const s = CFG.customer ? await getSession().catch(() => null) : null;
    const h = { 'Content-Type': 'image/jpeg', 'cache-control': 'max-age=3600', 'x-upsert': 'false' };
    if (KEY) h.apikey = KEY;
    h.Authorization = 'Bearer ' + ((s && s.access_token) || KEY);
    const r = await fetch(`${SB}/storage/v1/object/designs/${name}`, { method: 'POST', headers: h, body: blob });
    if (!r.ok) throw new Error('upload failed ' + r.status);
    return `${SB}/storage/v1/object/public/designs/${name}`;
  }

  async function addToCart() {
    const v = currentVariant();
    if (!v || !placements.length) return;
    // Untouched template: the shopper is buying the print exactly as shown, so add that product itself
    // (what its own buy button did), not a custom AI line.
    const norm = (x) => String(x).toLowerCase().replace(/\s+/g, '');
    const asIs = S.tpl && !S.tpl.edited && !S.tpl.personalized && CFG.template &&
      (CFG.template.variants || []).find((tv) => tv.available && norm(tv.title) === norm(v.title));
    if (asIs) {
      try {
        const r = await fetch(`${CFG.root.replace(/\/$/, '')}/cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: asIs.id, quantity: S.qty }] }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error('cart');
        S.added = true;
        renderAll();
        refreshCart(data);
      } catch (e) { alertMsg('We could not add this print to the cart. Please try again.'); }
      return;
    }
    const next = $('[data-pb-ai-next]');
    next.disabled = true;
    next.classList.add('is-loading');
    next.textContent = 'Saving your print…';
    alertMsg('');
    try {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const thumbName = `design-thumb-${id}.jpg`;
      const thumbBlob = await canvasBlob(THUMB_MULT, 0.7);
      const tileBlob = await canvasBlob(TILE_PX / CANVAS, 0.92);
      const [thumbUrl, tileUrl] = await Promise.all([upload(thumbName, thumbBlob), upload(`design-tile-${id}.jpg`, tileBlob)]);
      // The print sheet's address is known now; the relay makes it in a few seconds (its sweep remakes it if missed).
      const prodUrl = `${SB}/storage/v1/object/public/designs/print-${id}.jpg`;
      fetch(PRINT_RELAY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: tileUrl }), keepalive: true }).catch(() => {});
      // Same keys and order as handleConfirmAddToCart in PatternGenerator.tsx.
      const props = {
        '_customily-production-url': prodUrl,
        '_Design-URL': thumbUrl,
        'Theme': S.form.theme,
        'Occasion': S.form.occasion,
        'Name': S.tpl ? (S.tpl.personalized || '') : S.form.name,
      };
      if (S.form.message) props['Message'] = S.form.message;
      Object.assign(props, {
        'Art Style': S.form.artStyle || 'None',
        'Generation Mode': S.form.mode,
        'Pattern Template': S.layout,
        'Background Color': S.bg,
        'Created At': new Date().toISOString(),
        '_print-source': tileUrl,
      });
      const share = $('[data-pb-ai-share-ok]');
      if (share && share.checked && !$('[data-pb-ai-share]').hidden) {
        const b = await shareBlob();
        if (b) {
          props['_Share OK'] = 'Yes';
          props['_Share-URL'] = await upload(`design-share-${Date.now()}.jpg`, b);
        }
      }
      if (window.PBPreviewTag) window.PBPreviewTag(props); // checkout picture: see assets/pb.js
      const r = await fetch(`${CFG.root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: v.id, quantity: S.qty, properties: props }] }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data && (data.description || data.message) || 'cart failed');
      S.added = true;
      renderAll();
      saveToDesigns();
      saveToAccount(v, props).catch(() => {});
      refreshCart(data);
    } catch (e) {
      alertMsg('We could not add your print to the cart. Please try again.');
      renderSteps();
    }
  }

  // A print that goes in the cart is also kept on the shopper's account (Saved designs on the account page), the way
  // the name and logo designers do it: relay api/saved.js, proved by the theme's signed customer id
  // (sections/pb-account-sig.liquid). The studio's own "Your designs" list only lives in this browser.
  async function saveToAccount(variant, props) {
    if (!CFG.customer) return;
    const r = await fetch(`${CFG.root.replace(/\/$/, '')}/?sections=pb-account-sig&_=${Date.now()}`, { credentials: 'same-origin' });
    const m = /data-pb-as>([\s\S]*?)<\/script>/.exec((await r.json())['pb-account-sig'] || '');
    const who = m ? JSON.parse(m[1]) : null;
    if (!who) return;
    const name = props['Name'] || (S.form && S.form.name) || '';
    const design = {
      id: S.designId || newId(), variantId: variant.id, handle: (CFG.product && CFG.product.handle) || 'custom-ai-wrapping-paper',
      title: name ? `Custom AI Wrapping Paper for ${name}` : 'Custom AI Wrapping Paper', size: variant.title,
      thumb: props['_Design-URL'] || '', properties: props, editUrl: '',
    };
    await fetch(`${PRINT_RELAY.replace(/\/api\/print-file$/, '')}/api/saved`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'save', design, source: 'cart', who }) });
  }

  // ?pbsaved=<id>: a design the shopper saved in the old studio (Supabase saved_patterns, theirs only), opened here
  // from the account page. Same pieces and settings it was saved with.
  async function loadSaved(id) {
    const s = await getSession();
    if (!s) throw new Error('sign in');
    const rows = await rest(`saved_patterns?select=*&id=eq.${encodeURIComponent(id)}`, s.access_token);
    const t = rows[0];
    // Only the shopper's own saved design opens here, whatever the database would let anyone read.
    if (!t || (s.user_id && t.user_id !== s.user_id)) throw new Error('not found');
    const vmap = t.asset_version_map && Object.keys(t.asset_version_map).length ? t.asset_version_map : null;
    const map = vmap || Object.fromEntries((t.asset_ids || []).map((aid, i) => [i, [aid]]));
    const chosen = t.selected_asset_versions || {};
    const hidden = new Set(t.hidden_assets || []);
    const ids = Object.values(map).flat();
    const assets = ids.length ? await rest(`generated_assets?select=id,file_url,asset_metadata,generation_prompt,name&id=in.(${ids.join(',')})`, s.access_token) : [];
    const byId = new Map(assets.map((a) => [a.id, a]));
    const pieces = [];
    Object.keys(map).sort((a, b) => a - b).forEach((slot) => {
      const sel = Array.isArray(chosen[slot]) && chosen[slot].length ? chosen[slot] : [0];
      map[slot].forEach((assetId, v) => {
        const a = byId.get(assetId);
        if (!a || !a.file_url) return;
        pieces.push({ uid: uid(), kind: 'ai', src: a.file_url, prompt: a.generation_prompt || null, isText: !!(a.asset_metadata && a.asset_metadata.isText),
          assetId, slot: Number(slot), picked: sel.includes(v) && !hidden.has(`${slot}-${v}`) });
      });
    });
    if (!pieces.length) throw new Error('no pieces');
    Object.assign(S, {
      designId: newId(), pieces, batchId: t.batch_id || null, step: 3, maxStep: 4, added: false, view: 'print', tpl: null,
      form: { occasion: t.occasion || '', name: t.recipient_name || '', theme: t.theme || '', message: t.message || '', artStyle: t.art_style || S.form.artStyle, mode: t.generation_mode || 'mixed' },
      bg: t.background_color || '#ffffff', myBgs: t.background_color ? [t.background_color] : [], layout: t.template_name || 'Horizontal Brick',
      sizeV: nearest(scaleOf, Number(t.scale) || 2.5), spaceV: nearest(densOf, 3.3 - (Number(t.density) || 2.5)), rot: Number(t.rotation) || 0,
    });
    dropBoxedPieces();
  }

  // ?pbbatch=<id>: one set of pieces the studio drew for this shopper (Supabase generated_assets, theirs only), opened
  // from the account page's "Design with AI sets". The pieces come back to step 2 to pick and arrange, as after a draw.
  async function loadBatch(batch) {
    const s = await getSession();
    if (!s) throw new Error('sign in');
    const rows = (await rest(`generated_assets?select=id,user_id,file_url,asset_metadata,generation_prompt,theme,occasion,name,message,art_style,generation_mode,asset_index&batch_id=eq.${encodeURIComponent(batch)}&order=asset_index.asc`, s.access_token))
      .filter((a) => a.file_url && (!s.user_id || a.user_id === s.user_id));
    if (!rows.length) throw new Error('not found');
    const pieces = rows.map((a, i) => ({ uid: uid(), kind: 'ai', src: a.file_url, prompt: a.generation_prompt || null,
      isText: !!(a.asset_metadata && a.asset_metadata.isText), assetId: a.id, slot: a.asset_index ?? i, picked: false }));
    pieces.slice(0, rows[0].generation_mode === 'text-only' ? 1 : 5).forEach((p) => { p.picked = true; });
    const f = rows[0];
    Object.assign(S, {
      designId: newId(), pieces, batchId: batch, step: 2, maxStep: 4, added: false, view: 'print', tpl: null,
      form: { occasion: f.occasion || '', name: f.name || '', theme: f.theme || '', message: f.message || '', artStyle: f.art_style || S.form.artStyle, mode: f.generation_mode || 'mixed' },
    });
  }

  async function refreshCart(added) {
    try {
      const cart = await (await fetch(`${CFG.root.replace(/\/$/, '')}/cart.js`)).json();
      document.dispatchEvent(new CustomEvent('pb:cart:added', { bubbles: true, detail: { items: added.items || [added], cart } }));
      if (window.PB && window.PB.cart) {
        // Foundation cart drawer (assets/pb.js) re-renders and opens itself.
        document.dispatchEvent(new CustomEvent('pb:cart:open'));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (e) { /* cart page still has it */ }
  }

  // ------------------------------------------------------------------ credits
  function openCredits(needMore) {
    const packs = CFG.creditPacks || [];
    const dlg = $('[data-pb-ai-credits-dialog]');
    $('[data-pb-ai-cd-title]').textContent = needMore ? 'You need a few more credits' : 'Get more credits';
    $('[data-pb-ai-cd-copy]').textContent = `Each new set of pieces uses ${CFG.creditsPerSet} credits. Redrawing one piece uses ${CFG.creditsPerRedraw}.` +
      (S.credits != null ? ` You have ${S.credits}.` : '');
    $('[data-pb-ai-packs]').innerHTML = packs.map((p) => `<button type="button" class="pb-ai__pack" data-pack="${p.variant}" data-credits="${p.credits}">
      <span class="pb-display pb-ai__pack-n">${p.credits}</span><span class="pb-ai__pack-l">credits</span><span class="pb-ai__pack-p">${money(p.price)}</span></button>`).join('');
    if (typeof dlg.showModal === 'function') dlg.showModal();
  }

  async function buyPack(btn) {
    btn.disabled = true;
    try {
      const r = await fetch(`${CFG.root.replace(/\/$/, '')}/cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.pack), quantity: 1, properties: { 'Credits': btn.dataset.credits, 'Product Type': 'Digital Credits' } }] }),
      });
      if (!r.ok) throw new Error('cart');
      const d = await r.json();
      btn.innerHTML = '<span class="pb-ai__pack-l">Added. Your credits arrive after checkout.</span>';
      refreshCart(d);
    } catch (e) {
      btn.disabled = false;
    }
  }

  // ------------------------------------------------------------------- events
  root.addEventListener('click', (e) => {
    const t = e.target.closest('button, [data-pb-ai-login]');
    if (!t || !root.contains(t)) return;
    const d = t.dataset;
    if ('pbAiNext' in d) {
      if (S.step === 1) { if (S.pieces.some((p) => p.kind === 'ai') && !S.busy) { readForm(); go(2); } else generate(); }
      else if (S.step === 4) addToCart();
      else go(S.step + 1);
    } else if ('pbAiBack' in d) go(S.step - 1);
    else if ('pbAiGo' in d) go(Number(d.pbAiGo));
    else if (d.pbAiOcc) { const f = $('[data-pb-ai-pane="1"]'); f.occasion.value = d.pbAiOcc; f.occasion.removeAttribute('aria-invalid'); readForm(); saveDraft(); }
    else if ('pbAiMsgOpen' in d) { showMessageField(); $('#pb-ai-msg').focus(); }
    else if (d.style) { S.form.artStyle = d.style; renderStyles(); saveDraft(); }
    else if (d.toggle) { const p = S.pieces.find((x) => x.uid === d.toggle); if (p) { p.picked = !p.picked; markEdited(); relayout(true); renderAll(); saveDraft(); } }
    else if (d.redraw) redraw(d.redraw);
    else if (d.remove) { markEdited(); S.pieces = S.pieces.filter((x) => x.uid !== d.remove); relayout(true); renderAll(); saveDraft(); }
    else if ('pbAiDrawMore' in d) generate();
    else if ('pbAiTextOpen' in d) { $('[data-pb-ai-texter]').hidden = false; renderTexter(); loadFonts(); $('#pb-ai-text').focus(); }
    else if ('pbAiTextCancel' in d) $('[data-pb-ai-texter]').hidden = true;
    else if (d.font) { texter.font = d.font; renderTexter(); }
    else if (d.ink) { texter.ink = d.ink; renderTexter(); }
    else if ('pbAiTextAdd' in d) {
      const txt = $('#pb-ai-text').value.trim();
      if (!txt) { $('#pb-ai-text').focus(); return; }
      textToPiece(txt).then((src) => { addPiece({ kind: 'text', src, isText: true }); $('#pb-ai-text').value = ''; $('[data-pb-ai-texter]').hidden = true; });
    }
    else if (d.bg) { markEdited(); S.bg = d.bg; renderColors(); draw(); saveDraft(); }
    else if (d.layout) { markEdited(); S.layout = d.layout; relayout(true); renderLayouts(); draw(); saveDraft(); }
    else if ('pbAiShuffle' in d) { markEdited(); relayout(true); draw(); }
    else if ('pbAiZoom' in d) { S.zoom = !S.zoom; t.setAttribute('aria-pressed', String(S.zoom)); t.textContent = S.zoom ? 'One tile' : 'See it repeat'; draw(); }
    else if (d.roll) { S.roll = d.roll; S.added = false; renderRolls(); renderSteps(); saveDraft(); }
    else if (d.pbAiQty) { S.qty = Math.max(1, Math.min(20, S.qty + Number(d.pbAiQty))); S.added = false; renderRolls(); renderSteps(); saveDraft(); }
    else if ('pbAiBuy' in d) openCredits(false);
    else if (d.pack) buyPack(t);
    else if ('pbAiRestart' in d || 'pbAiReset' in d) startNew();
    else if ('pbAiStylesMore' in d) { S.stylesOpen = !S.stylesOpen; renderStyles(); }
    else if ('pbAiDesigns' in d) openDesigns();
    else if (d.openDesign) openDesign(d.openDesign);
    else if (d.delDesign) {
      designs = designs.filter((x) => x.id !== d.delDesign);
      idb.set(DESIGNS_KEY, designs);
      renderDesignsCount();
      renderDesignsList();
    }
    else if (d.pbAiView) setView(d.pbAiView);
    else if ('pbAiTnameGo' in d) personalize();
    else if ('pbAiLogin' in d) { e.preventDefault(); sendToLogin(); }
  });

  root.addEventListener('input', (e) => {
    const t = e.target;
    if (t.matches('[data-pb-ai-size], [data-pb-ai-space], [data-pb-ai-rot], [data-bg-any]')) markEdited();
    if (t.matches('#pb-ai-tname-in')) { S.form.name = t.value.trim(); return; }
    if (t.matches('[data-pb-ai-size]')) { S.sizeV = Number(t.value); relayout(false); renderSliders(); draw(); }
    else if (t.matches('[data-pb-ai-space]')) { S.spaceV = Number(t.value); relayout(true); renderSliders(); draw(); }
    else if (t.matches('[data-pb-ai-rot]')) { S.rot = Number(t.value); relayout(false); renderSliders(); draw(); }
    else if (t.matches('[data-bg-any]')) { S.bg = t.value; draw(); }
    else if (t.closest('[data-pb-ai-pane="1"]')) { t.removeAttribute('aria-invalid'); readForm(); }
    else return;
    saveDraft();
  });
  root.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-bg-any]')) {
      S.bg = t.value;
      if (!S.myBgs.some((c) => c.toLowerCase() === S.bg.toLowerCase())) S.myBgs = S.myBgs.concat(S.bg).slice(-6);
      renderColors();
      saveDraft();
    }
    else if (t.matches('[data-pb-ai-photo]') && t.files && t.files[0]) {
      fileToPiece(t.files[0]).then((src) => addPiece({ kind: 'photo', src, isText: false })).catch(() => alertMsg('We could not read that photo. Try a JPG or PNG.'));
      t.value = '';
    } else if (t.closest('[data-pb-ai-pane="1"]')) { readForm(); saveDraft(); }
  });
  $('[data-pb-ai-pane="1"]').addEventListener('submit', (e) => { e.preventDefault(); generate(); });

  // Gallery "Make one like this" (pb-ai-gallery section) and any other page can prefill the form.
  document.addEventListener('pbai:prefill', async (e) => {
    const d = e.detail || {};
    if (S.pieces.length) await startNew(); // keep the current creation under Your designs
    ['occasion', 'name', 'theme', 'message'].forEach((k) => { if (d[k] != null) S.form[k] = String(d[k]); });
    if (d.artStyle && styles.some((x) => x.slug === d.artStyle)) S.form.artStyle = d.artStyle;
    else if (d.artStyle) pendingStyle = d.artStyle;
    writeForm();
    go(1);
    requestAnimationFrame(() => root.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    const f = $('[data-pb-ai-pane="1"]');
    (S.form.name ? f.theme : f.name).focus({ preventScroll: true });
    saveDraft();
  });

  // --------------------------------------------------------------------- boot
  (async () => {
    const params = new URLSearchParams(location.search);
    const d = await idb.get(DRAFT_KEY);
    if (d && Date.now() - d.t < 30 * 864e5 && (!CFG.template || (d.tpl && d.pieces && d.pieces.length))) applyDraft(d);
    else if (CFG.template) {
      try { await loadTemplate(); }
      catch (e) { S.designId = newId(); alertMsg('We could not load this print into the studio. You can still design your own below.'); }
    } else S.designId = newId();
    await loadDesigns();
    if (params.get('pbbatch') && CFG.customer) {
      try { if (S.pieces.length) await saveToDesigns(); await loadBatch(params.get('pbbatch')); }
      catch (e) { alertMsg('We could not open that set. It may belong to a different account.'); }
    }
    if (params.get('pbsaved') && CFG.customer) {
      try { if (S.pieces.length) await saveToDesigns(); await loadSaved(params.get('pbsaved')); }
      catch (e) { alertMsg('We could not open that saved design. It may belong to a different account.'); }
    }
    // "Make one like this" from another page: start a fresh design (the current one is kept) with the idea's answers.
    // Also "Design a print for Drew" from the account page (?pbfor=&pblikes=&pbocc=): the person's name, what they
    // love and the occasion replace whatever was typed for an earlier design, which is kept under Your designs.
    const forPerson = params.get('pbfor');
    if (params.get('occasion') || params.get('theme') || forPerson) {
      if (S.pieces.length) await startNew();
      if (forPerson) Object.assign(S.form, { occasion: (params.get('pbocc') || '').slice(0, 40), name: forPerson.slice(0, 40), theme: (params.get('pblikes') || '').slice(0, 240), message: '' });
      ['occasion', 'name', 'theme', 'message'].forEach((k) => { if (params.get(k)) S.form[k] = params.get(k).slice(0, 140); });
      if (params.get('style')) pendingStyle = params.get('style');
      S.step = 1;
    }
    writeForm();
    relayout(true);
    renderAll();
    loadStyles();
    if (CFG.customer && KEY) getSession().then(() => loadCredits(true)).catch(() => {});
    else if (!CFG.customer) { $('[data-pb-ai-credits]').hidden = true; }
    if (!KEY) alertMsg('The AI studio is not connected yet, so drawing new pieces is paused. You can still look around.');
  })();
})();
